/**
 * The bridge from simulation to presentation.
 *
 * One direction only: this reads simulation state and drains its event queue, then tells
 * the renderer what to draw and the audio graph what to play. Nothing here ever writes
 * back into the sim. That constraint is what lets the whole game run headless in tests —
 * if this file did not exist, the sim would still be correct.
 *
 * Events are drained exactly once per frame. A frame that renders at 30 Hz while the sim
 * ticks at 60 Hz simply sees two ticks' worth of events at once, which is correct: a
 * splash that happened is a splash that should be drawn.
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import type { RenderWorld } from './world';
import type { SimWorld } from '../sim/world';
import type { SimEvent } from '../sim/events';
import type { AudioEngine } from '../audio/audioEngine';
import type { Haptics } from '../app/haptics';
import { TORPEDO } from '../content/weapons';
import { KRAKEN } from '../content/boss';
import { commsUrl } from '../content/radio';
import { WeaponMounts } from './rig/weaponMounts';
import { underwaterBurstBeats } from './fx/effects';

export interface SyncOptions {
  world: RenderWorld;
  sim: SimWorld;
  audio?: AudioEngine | null;
}

export class SimRenderSync {
  private readonly world: RenderWorld;
  private readonly sim: SimWorld;
  private audio: AudioEngine | null;

  /** Weather is only pushed when it actually changes — rebuilding the wave field is not free. */
  private lastSeaState = -1;
  private lastFog = -1;
  private lastTimeOfDay = -1;

  /** Radio and objective lines the HUD consumes. */
  readonly messages: Array<{ speaker: string; text: string; at: number }> = [];

  /** Sim time of the last refused unlocked torpedo shot — the scope flashes LOCK TARGET. */
  lockWarnAt: number | null = null;

  /** The dying Kraken's water entry fires its splash exactly once per fall. */
  private krakenFallSplashed = false;

  /**
   * Sim time of the player's last 30 mm round into a hostile hull — the reticle's hit
   * tick. Hostile hulls are only ever struck by the player, so faction is the whole
   * test; friendly-fire hits deliberately do not tick, because a confirmation chirp for
   * hitting your own tanker would be the interface congratulating a mistake.
   */
  hullHitAt: number | null = null;

  /**
   * Searchlight depression, radians. The simulation only tracks bearing — depression is
   * purely how the beam lands on the water, so it lives here.
   *
   * The useful band is narrow and entirely below horizontal. From 105 m up, the sea at
   * 200 m sits 27 degrees down and the sea at 1400 m sits 4 degrees down, so anything
   * shallower than about 4 degrees misses the water altogether and throws the beam off
   * to the horizon. The default lands the pool around 850 m — inside the gun's working
   * envelope, which is where a contact worth lighting up will be.
   */
  searchlightPitch = -0.12;

  /**
   * Live screw-whine voices, keyed by projectile id.
   *
   * Held here rather than in the audio engine because only this layer knows when a
   * torpedo stops existing: the pool reuses slots silently, so a voice that is not
   * reaped the moment its weapon leaves the pool would run forever.
   */
  private readonly torpedoVoices = new Map<
    number, { setSpeed: (s: number) => void; setDistance: (m: number) => void; stop: () => void }
  >();

  /** Android haptics, when the device has a motor. Silent on iOS by platform limitation. */
  haptics: Haptics | null = null;

  /**
   * The visible gun and projector on their sponsons.
   *
   * Owned here rather than by the render world because they are driven entirely by
   * simulation state — the aim bearing every frame, and the fire events as they drain —
   * and this is the one layer allowed to read both sides.
   */
  readonly mounts: WeaponMounts;

  constructor(opts: SyncOptions) {
    this.world = opts.world;
    this.sim = opts.sim;
    this.audio = opts.audio ?? null;
    // Wreck traces and burning columns belong to one watch. A new sync means a new
    // simulation/shift, and neither should be inherited by it.
    this.world.vessels.clearWreckTraces();
    this.world.effects.clearSmokeColumns();
    this.world.clearRigImpactCamera();
    this.mounts = new WeaponMounts(opts.world.scene);
  }

  setAudio(audio: AudioEngine | null): void {
    this.audio = audio;
  }

  /**
   * Release everything this sync owns in the scene.
   *
   * A shift start builds a fresh sim and a fresh sync over the same RenderWorld, so
   * without this the previous sync's weapon mounts stay in the scene forever: a second
   * gun, never updated again, frozen at the bearing it held when the shift began. Adam
   * saw exactly that — "two barrels, one moving and one stuck".
   */
  dispose(): void {
    this.stopWeaponVoices();
    this.world.clearRigImpactCamera();
    this.mounts.dispose();
  }

  /** Called once per rendered frame, after the sim has been ticked. */
  update(dt: number): void {
    const sim = this.sim;
    const world = this.world;

    // Keep the renderer's clock locked to the simulation's, so waves under a hull are the
    // waves the hull is floating on — then hand that clock to the shaders. Setting the
    // field alone leaves the ocean rendering at whatever time it was built with.
    world.time = sim.time;
    world.updateRigImpactCamera(sim.time);
    world.pushFrame();

    this.syncWeather();
    world.setRigIntegrity(sim.resources.integrity, sim.time);

    // The searchlight the simulation credits for spotting is the searchlight the player
    // sees on the water — one bearing, read here, driving both.
    world.setSearchlight(sim.spotlightBearing, this.searchlightPitch);

    world.effects.setCamera(world.camera.position);

    world.vessels.sync(
      sim.vessels,
      dt,
      (x, z) => sim.surfaceAt(x, z),
      sim.time,
      sim.kraken ? { phase: sim.kraken.phase, timer: sim.kraken.timer } : null,
    );

    /*
     * The finale's two sustained voices, reported every frame rather than switched on
     * transitions: the assault bed while limbs are on the structure, and the stare while
     * the eye holds the player. Both are idempotent, so this is just "what is true now".
     *
     * The stare follows `eyeExposed` rather than the phase, so the sound arrives exactly
     * when the eye opens — the rise spends its first four seconds still coming up.
     */
    const phase = sim.kraken?.phase ?? null;
    this.audio?.setKrakenClimb(phase === 'tentacles');
    this.audio?.setKrakenStare(phase === 'rise' && sim.kraken?.eyeExposed === true);

    /*
     * The death splash — a colossal mass hitting the water partway through its fall.
     *
     * Timer-crossing rather than event-driven, because the fall is a continuous pose the
     * renderer owns; the simulation only knows the phase. Fires once per dying phase.
     */
    if (phase === 'dying' && sim.kraken) {
      const splashAt = KRAKEN.eyeRollSeconds + KRAKEN.fallSeconds * 0.45;
      if (!this.krakenFallSplashed && sim.kraken.timer >= splashAt) {
        this.krakenFallSplashed = true;
        const boss = sim.vessels.find((v) => v.kind === 'kraken');
        if (boss) {
          world.effects.krakenSlam(boss.pos.x, boss.pos.z);
          this.audio?.krakenOceanImpact();
          this.haptics?.play('impact', sim.time);
        }
      }
    }

    world.effects.update(
      dt, sim.projectiles.items, sim.vessels, (x, z) => sim.surfaceAt(x, z),
    );

    this.syncTorpedoVoices();
    this.syncBattleAmbience();
    this.mounts.update(dt, sim.aimDir, sim.rig.selectedWeapon);

    for (const event of sim.events.drain()) this.handleEvent(event);
  }

  /**
   * Push weather into the renderer only when a value the visuals depend on has moved.
   * A blend runs at 60 Hz; rebuilding the wave field that often would be both wasteful
   * and wrong, since the field is what vessels are floating on.
   */
  private syncWeather(): void {
    const w = this.sim.weather;
    const seaChanged = Math.abs(w.seaState - this.lastSeaState) > 0.02;
    const fogChanged = Math.abs(w.fogDensity - this.lastFog) > 0.005;
    const timeChanged = Math.abs(w.timeOfDay - this.lastTimeOfDay) > 0.005;
    if (!seaChanged && !fogChanged && !timeChanged) return;

    this.lastSeaState = w.seaState;
    this.lastFog = w.fogDensity;
    this.lastTimeOfDay = w.timeOfDay;
    // Only rebuild the wave field when the sea itself changed; fog and light do not
    // move water.
    this.world.setWeather(w, seaChanged);
  }

  private handleEvent(e: SimEvent): void {
    const world = this.world;
    const fx = world.effects;

    switch (e.type) {
      case 'WeaponFired': {
        if (!e.hostile) {
          if (e.weapon === 'torpedo') {
            fx.tubeBlast(e.pos.x, e.pos.y, e.pos.z, e.dir.x, e.dir.z);
            this.audio?.torpedoLaunch();
          } else if (e.weapon === 'autocannon') {
            fx.muzzleFlash(e.pos.x, e.pos.y, e.pos.z, e.dir.x, e.dir.y, e.dir.z);
            this.audio?.autocannonShot();
            this.mounts.gunFired();
          } else {
            // The projector. Its recording stands in for the placeholder clang this
            // branch used to borrow from the hull-impact voice.
            if (!this.audio?.depthChargeLaunch()) {
              this.audio?.hullImpact(this.distanceTo(e.pos.x, e.pos.y, e.pos.z));
            }
            this.mounts.projectorFired();
          }
          this.haptics?.play(
            e.weapon === 'autocannon' ? 'burst' : 'launch', this.sim.time,
          );
        } else {
          fx.hostileMuzzleFlash(
            e.pos.x,
            e.pos.y,
            e.pos.z,
            e.dir.x,
            e.dir.y,
            e.dir.z,
            e.sourceKind,
            e.sourceHeading,
          );
          this.audio?.hostileShot(this.distanceTo(e.pos.x, e.pos.y, e.pos.z));
        }
        break;
      }

      case 'ImpactWater': {
        const big = e.kind !== 'shell';
        fx.waterImpact(e.pos.x, e.pos.y, e.pos.z, e.speed, big, e.ricochet);
        const splashDistance = this.distanceTo(e.pos.x, e.pos.y, e.pos.z);
        // A depth charge entering the water is a moment the player is waiting on — it
        // says the lob was good and starts the sink — so it gets Adam's recording
        // rather than the generic column. Everything else keeps the synthesised one.
        const recorded = e.kind === 'depthCharge'
          && (this.audio?.depthChargeSplash(splashDistance) ?? false);
        if (!recorded) this.audio?.waterImpact(splashDistance, big);
        break;
      }

      case 'ImpactHull': {
        const struck = this.sim.vessels.find((v) => v.id === e.targetId);
        // A creature bleeds; a hull sparks. Same event, different matter (Adam's note:
        // shooting the Kraken looked exactly like shooting a ship).
        if (struck && (struck.kind === 'kraken' || struck.kind === 'tentacle')) {
          fx.fleshImpact(e.pos.x, e.pos.y, e.pos.z);
        } else {
          fx.hullImpact(
            e.pos.x,
            e.pos.y,
            e.pos.z,
            e.ricochet,
            e.targetId % 2 === 0 ? -1 : 1,
          );
        }
        if (e.kind === 'shell') {
          if (struck && struck.faction === 'hostile') this.hullHitAt = this.sim.time;
        }
        // A torpedo finding a hull is the payoff of a thirty-second wait; it gets its
        // recorded detonation rather than the generic clang, and a 50 ms flash at the
        // point of impact. Same speed-of-sound delay on the audio either way.
        const hullDistance = this.distanceTo(e.pos.x, e.pos.y, e.pos.z);
        if (e.kind === 'torpedo' && e.damage > 0) {
          fx.torpedoHitFlash(e.pos.x, e.pos.y, e.pos.z);
          this.audio?.torpedoHit(hullDistance);
        } else {
          // The 30 mm gets Adam's recorded strike, one voice per round: the rhythm of
          // hits against misses is the feedback the gunner is reading. Incoming fire and
          // anything else keeps the clustered synthesised clang.
          const recorded = e.kind === 'shell' && (this.audio?.hullHit(hullDistance) ?? false);
          if (!recorded) this.audio?.hullImpact(hullDistance);
        }
        break;
      }

      case 'Detonation': {
        const distance = this.distanceTo(e.pos.x, e.pos.y, e.pos.z);
        let recorded = false;
        if (e.underwater) {
          // The beat sequence schedules itself; the surface eruption is one of its
          // later beats, so nothing is drawn at the surface here.
          fx.underwaterBurst(e.pos.x, e.pos.z, e.depth);
          /*
           * Two recordings on the two beats the player watches — the sea lifting, then
           * the column breaking — taking their offsets from the FX layer's own schedule
           * so picture and sound cannot drift apart. Each still travels at the game's
           * speed of sound on top of that. If the files are missing this returns false
           * and the synthesised burst covers for them.
           */
          const beats = underwaterBurstBeats(e.depth);
          recorded = this.audio?.depthChargeBurst(distance, beats.lift, beats.column) ?? false;
        } else {
          fx.detonation(e.pos.x, e.pos.y, e.pos.z, e.radius);
        }
        if (!recorded) this.audio?.detonation(distance, e.underwater, e.depth);
        if (e.underwater) this.haptics?.play('detonation', this.sim.time);
        break;
      }

      case 'ShipSafe': {
        this.audio?.klaxon();
        this.push('RIG CONTROL', 'Vessel inside the safety zone. Beginning transfer.');
        break;
      }

      case 'CargoDelivered':
        this.push('CARGO', e.summary);
        break;

      case 'ShipLost': {
        // The detonation and smoke are driven by the vessel's own state, not by this
        // event, so a wreck keeps burning long after the message has scrolled away.
        this.push('RIG CONTROL', e.faction === 'friendly' ? 'We have lost her.' : 'Splash one.');
        break;
      }

      case 'RigHit': {
        world.triggerRigImpact(e.pos.x, e.pos.z, e.damage, e.cause, this.sim.time);
        // Incoming rounds get Adam's ricochet takes; the creature climbing the rig gets
        // his crush takes, one at a time with clear air between them. The synthesised
        // clang stands in whenever the recordings failed to load.
        const recorded = e.cause === 'gunfire'
          ? this.audio?.rigBulletHit() ?? false
          : this.audio?.krakenCrush() ?? false;
        if (!recorded) this.audio?.rigHit();
        this.haptics?.play('impact', this.sim.time);
        if (e.casualties > 0) {
          this.push('DAMAGE CONTROL', `Hit taken — ${e.casualties} casualties.`);
        }
        break;
      }

      case 'Radio': {
        this.push(e.speaker, e.text);
        /*
         * The voice is the presentation of the line, never a gate on it: the text goes up
         * the instant the sim says so, and the take plays when the channel is free. A
         * line with no `read` is spoken as written.
         */
        const url = commsUrl(e.read ?? e.text);
        if (e.final) this.audio?.radioFinal(url);
        else this.audio?.radio(url);
        break;
      }

      case 'Objective':
        this.push('OBJECTIVE', e.text);
        break;

      case 'WeaponState':
        if (e.state === 'reloading') this.audio?.reload();
        if (e.state === 'noLock') this.lockWarnAt = this.sim.time;
        break;

      case 'KrakenPhase': {
        // The slam is the one the player must see: it costs integrity, and damage with
        // no visible cause reads as arbitrary.
        if (e.slam) {
          fx.krakenSlam(e.pos.x, e.pos.z);
          this.audio?.detonation(this.distanceTo(e.pos.x, 0, e.pos.z), false, 0);
          this.haptics?.play('impact', this.sim.time);
        } else if (e.phase === 'mantle') {
          // Surfacing: a great deal of water displaced at once.
          fx.krakenSlam(e.pos.x, e.pos.z);
        } else if (e.phase === 'rise') {
          // The head coming up out of the dark, once.
          this.audio?.krakenHeadReveal();
        } else if (e.phase === 'dying') {
          // The eye rolling back and the mass starting to go over.
          this.audio?.krakenDying();
        } else if (e.phase === 'gone') {
          this.krakenFallSplashed = false;
        }
        break;
      }

      case 'ContactNew':
      case 'ContactIdentified':
      case 'UpgradeFitted':
      case 'ShiftEnd':
        break;
    }
  }

  /**
   * Track the screw whine of every torpedo in the water, and reap the rest.
   *
   * The pitch follows the weapon's own speed through its acceleration curve, which is
   * what makes the sound report the thing the player cannot otherwise see: how far
   * through its run the fish is.
   */
  private syncTorpedoVoices(): void {
    const live = new Set<number>();

    for (const p of this.sim.projectiles.items) {
      if (!p.active || p.kind !== 'torpedo' || p.phase === 'air') continue;
      live.add(p.id);

      let voice = this.torpedoVoices.get(p.id);
      if (!voice) {
        const started = this.audio?.torpedoRun();
        if (!started) continue;
        voice = started;
        this.torpedoVoices.set(p.id, voice);
      }
      const speed = Math.hypot(p.vel.x, p.vel.z);
      voice.setSpeed((speed - TORPEDO.speedStart) / (TORPEDO.speedMax - TORPEDO.speedStart));
      voice.setDistance(this.distanceTo(p.pos.x, p.pos.y, p.pos.z));
    }

    for (const [id, voice] of this.torpedoVoices) {
      if (live.has(id)) continue;
      voice.stop();
      this.torpedoVoices.delete(id);
    }
  }

  /**
   * Distant gunfire while the convoy is being worked over.
   *
   * The cue is a hostile in its attacking state whose target is a friendly hull — the
   * moment the boats have actually closed and started shooting, not merely the moment
   * they set course. Range is measured to the victim rather than to the attacker,
   * because the ship being hit is what the player is listening for and what they will
   * turn the scope onto. Nearest engagement wins when several are running.
   */
  private syncBattleAmbience(): void {
    let nearest: number | null = null;
    for (const v of this.sim.vessels) {
      /*
       * 'attacking' alone missed the FAC entirely. Her pattern is volley-then-jink, so
       * she holds 'attacking' for one tick per volley and spends the rest of a fight
       * she is very much conducting in 'evading' — the bed's 0.6 s smoothing never
       * heard the flicker, and a tanker could be worked over in ambient silence.
       * Both states are the fight; only hostiles ever use 'evading'.
       */
      if (v.faction !== 'hostile') continue;
      if (v.state !== 'attacking' && v.state !== 'evading') continue;
      const victim = this.sim.vessels.find((t) => t.id === v.targetId);
      if (!victim || victim.faction !== 'friendly') continue;
      if (victim.state === 'dead' || victim.state === 'departing') continue;
      const range = this.distanceTo(victim.pos.x, victim.pos.y, victim.pos.z);
      if (nearest === null || range < nearest) nearest = range;
    }
    this.audio?.setBattleAmbience(nearest);
  }

  /** Silence every running weapon voice — a shift ending must not leave one whining. */
  stopWeaponVoices(): void {
    for (const voice of this.torpedoVoices.values()) voice.stop();
    this.torpedoVoices.clear();
    // The battle bed is a running voice too, and a shift ending mid-engagement would
    // otherwise leave it sounding over the report screen. Same for a crush tail.
    this.audio?.setBattleAmbience(null);
    this.audio?.stopCreatureVoices();
  }

  private distanceTo(x: number, y: number, z: number): number {
    return Vector3.Distance(this.world.camera.position, new Vector3(x, y, z));
  }

  private push(speaker: string, text: string): void {
    this.messages.push({ speaker, text, at: this.sim.time });
    // The log is a rolling window; the HUD only ever shows the last few lines.
    if (this.messages.length > 40) this.messages.shift();
  }
}
